import type { FilterField, FilterValue, AppliedFilters } from './types';

/**
 * Render a filter as the sentence its chip shows: operator, then value.
 *
 * Kept separate from the components so the wording is testable on its own —
 * a chip that reads wrongly is a filter someone can't verify at a glance, and
 * it's the part most likely to drift as field types are added.
 */
export function describeFilter(field: FilterField, value: FilterValue): { operator: string; value: string } | null {
  if (field.type === 'enum' && value.type === 'enum') {
    if (value.values.length === 0) return null;
    const labels = value.values.map(
      (candidate) => field.options.find((option) => option.value === candidate)?.label ?? candidate,
    );
    return {
      operator: 'is',
      // "A, B or C" — the last separator is "or", because these are alternatives.
      value:
        labels.length === 1
          ? labels[0]!
          : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`,
    };
  }

  if (field.type === 'number' && value.type === 'number') {
    const prefix = field.prefix ?? '';
    const has = (candidate: number | undefined): candidate is number =>
      candidate !== undefined && !Number.isNaN(candidate);

    if (has(value.min) && has(value.max)) {
      return { operator: 'is between', value: `${prefix}${value.min} and ${prefix}${value.max}` };
    }
    if (has(value.min)) return { operator: 'is over', value: `${prefix}${value.min}` };
    if (has(value.max)) return { operator: 'is under', value: `${prefix}${value.max}` };
    return null;
  }

  if (field.type === 'date' && value.type === 'date') {
    if (value.presetDays !== undefined) {
      const preset = field.presets?.find((candidate) => candidate.days === value.presetDays);
      // The preset's own label is title case because it heads a button in a
      // list; dropped into the middle of a sentence it read "Created in Last
      // 30 days". The chip is a sentence, so it gets sentence case.
      const label = preset?.label ?? `last ${value.presetDays} days`;
      return { operator: 'in', value: label.charAt(0).toLowerCase() + label.slice(1) };
    }
    if (value.from && value.to) return { operator: 'between', value: `${value.from} – ${value.to}` };
    if (value.from) return { operator: 'after', value: value.from };
    if (value.to) return { operator: 'before', value: value.to };
    return null;
  }

  return null;
}

/** Filters that describe to nothing are treated as unset. */
export function activeFilterKeys(fields: FilterField[], filters: AppliedFilters): string[] {
  return Object.keys(filters).filter((key) => {
    const field = fields.find((candidate) => candidate.key === key);
    const value = filters[key];
    return !!field && !!value && describeFilter(field, value) !== null;
  });
}
